import { useEffect, useRef } from 'react';

/**
 * AWS CloudWatch RUM utility functions for custom event tracking.
 */
export type RumMetadata = Record<string, unknown>;

export type RumUserIdentity = {
  email?: string | null;
  cognitoSub?: string | null;
  cognitoUsername?: string | null;
};

type WebVitalPayload = {
  name: string;
  value: number;
  id?: string;
  delta?: number;
  rating?: string;
  navigationType?: string;
};

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(normalized)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

type AwsRumClient = {
  addSessionAttributes?: (attrs: Record<string, string | number | boolean>) => void;
  recordEvent?: (eventType: string, eventData: object) => void;
  dispatch?: () => void;
};

let hasWarnedRumUnavailable = false;

function getAwsRumClient(): AwsRumClient | null {
  const awsRum = (window as any).awsRum as AwsRumClient | undefined;
  if (!awsRum || typeof awsRum.recordEvent !== 'function') {
    if (process.env.NODE_ENV !== 'production' && !hasWarnedRumUnavailable) {
      hasWarnedRumUnavailable = true;
      console.warn(
        'CloudWatch RUM is not initialized — custom funnel events are skipped. ' +
          'Set REACT_APP_RUM_APPLICATION_ID in the build environment.'
      );
    }
    return null;
  }
  return awsRum;
}

/**
 * Attach authenticated user context to all RUM events for this browser session.
 * Values land in event metadata (not user_details.userId, which RUM generates anonymously).
 */
export function setRumUserIdentity(identity: RumUserIdentity): void {
  try {
    const awsRum = getAwsRumClient();
    if (!awsRum) {
      return;
    }

    const email =
      typeof identity.email === 'string' && identity.email.trim().length > 0
        ? identity.email.trim()
        : undefined;
    const cognitoSub =
      typeof identity.cognitoSub === 'string' && identity.cognitoSub.trim().length > 0
        ? identity.cognitoSub.trim()
        : undefined;
    const cognitoUsername =
      typeof identity.cognitoUsername === 'string' && identity.cognitoUsername.trim().length > 0
        ? identity.cognitoUsername.trim()
        : undefined;

    const isAuthenticated = Boolean(email || cognitoSub || cognitoUsername);
    const attrs: Record<string, string> = {
      auth_state: isAuthenticated ? 'authenticated' : 'anonymous',
    };

    if (email) attrs.user_email = email;
    if (cognitoSub) attrs.cognito_sub = cognitoSub;
    if (cognitoUsername) attrs.cognito_username = cognitoUsername;

    awsRum.addSessionAttributes!(attrs);
  } catch (error) {
    console.error('Error setting RUM user identity:', error);
  }
}

export function setRumUserIdentityFromIdToken(idToken: string): void {
  const claims = parseJwtPayload(idToken);
  if (!claims) {
    return;
  }

  const emailClaim = claims.email;
  const subClaim = claims.sub;
  const usernameClaim = claims['cognito:username'];

  setRumUserIdentity({
    email: typeof emailClaim === 'string' ? emailClaim : null,
    cognitoSub: typeof subClaim === 'string' ? subClaim : null,
    cognitoUsername: typeof usernameClaim === 'string' ? usernameClaim : null,
  });
}

export function clearRumUserIdentity(): void {
  setRumUserIdentity({});
}

function withDefaultMetadata(metadata?: RumMetadata): RumMetadata {
  const payload: RumMetadata = {
    timestamp: new Date().toISOString(),
    ...(metadata || {}),
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
}

type RecordRumEventOptions = {
  /** Flush the RUM batch immediately (useful for funnel milestones). */
  flush?: boolean;
};

/**
 * Safely record a custom RUM event.
 * Requires the app monitor to have custom events enabled.
 */
export function recordRumEvent(
  eventType: string,
  metadata?: RumMetadata,
  options?: RecordRumEventOptions
): void {
  try {
    const awsRum = getAwsRumClient();
    if (!awsRum?.recordEvent) {
      return;
    }

    awsRum.recordEvent(eventType, withDefaultMetadata(metadata));
    if (options?.flush && typeof awsRum.dispatch === 'function') {
      awsRum.dispatch();
    }
  } catch (error) {
    console.error('Error recording RUM event:', error);
  }
}

/**
 * Record a page view event.
 */
export function recordPageView(pageName: string, metadata?: RumMetadata): void {
  recordRumEvent('v2_page_view', {
    action: 'page_view',
    page: pageName,
    ...metadata,
  });
}

/** @deprecated Prefer recordFunnelStep / recordRumEvent with a v2_* event type. */
export function recordUserAction(action: string, metadata?: RumMetadata): void {
  recordRumEvent('v2_action', {
    action,
    ...metadata,
  });
}

export function recordFunnelStep(
  stepName: string,
  stepStatus: 'start' | 'success' | 'failure' | 'blocked' | 'abandon' | 'shown' | 'click',
  metadata?: RumMetadata
): void {
  recordRumEvent(
    'v2_funnel',
    {
      action: 'funnel_step',
      step_name: stepName,
      step_status: stepStatus,
      ...metadata,
    },
    { flush: true }
  );
}

export function recordErrorStage(
  errorStage: string,
  error: unknown,
  metadata?: RumMetadata
): void {
  const err = error as { name?: string; code?: string; message?: string } | null;
  recordRumEvent('v2_error', {
    action: 'funnel_error',
    error_stage: errorStage,
    error_code: err?.code || err?.name || 'unknown_error',
    error_message: err?.message || 'Unknown error',
    ...metadata,
  });
}

export function recordLatency(
  operation: string,
  durationMs: number,
  metadata?: RumMetadata
): void {
  recordRumEvent('v2_latency', {
    action: 'latency_measurement',
    operation,
    duration_ms: Math.round(durationMs),
    ...metadata,
  });
}

export function recordWebVital(metric: WebVitalPayload, metadata?: RumMetadata): void {
  recordRumEvent('v2_web_vital', {
    action: 'web_vital',
    metric_name: metric.name,
    metric_value: metric.value,
    metric_delta: metric.delta,
    metric_id: metric.id,
    metric_rating: metric.rating,
    navigation_type: metric.navigationType,
    ...metadata,
  });
}

/**
 * Record time spent on a page or funnel step.
 */
export function recordPageDwell(pageName: string, durationMs: number, metadata?: RumMetadata): void {
  recordRumEvent('v2_dwell', {
    action: 'page_dwell',
    page: pageName,
    duration_ms: Math.round(durationMs),
    ...metadata,
  });
}

/**
 * Track dwell time for a stable page: fires funnel shown on mount, page_dwell on unmount.
 */
export function useRumDwell(
  pageName: string,
  metadata?: RumMetadata,
  options?: { recordShown?: boolean }
): void {
  const startedAtRef = useRef(performance.now());
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    startedAtRef.current = performance.now();
    if (options?.recordShown !== false) {
      recordFunnelStep(pageName, 'shown', metadataRef.current);
    }

    return () => {
      recordPageDwell(pageName, performance.now() - startedAtRef.current, metadataRef.current);
    };
  }, [pageName, options?.recordShown]);
}

/**
 * Track dwell time across onboarding step changes.
 */
export function useRumStepDwell(
  stepName: string,
  stepIndex: number,
  metadata?: RumMetadata
): void {
  const startedAtRef = useRef(performance.now());
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    startedAtRef.current = performance.now();
    recordFunnelStep(stepName, 'shown', {
      funnel_step_index: stepIndex,
      ...metadataRef.current,
    });

    return () => {
      recordPageDwell(stepName, performance.now() - startedAtRef.current, {
        funnel_step_index: stepIndex,
        ...metadataRef.current,
      });
    };
  }, [stepName, stepIndex]);
}
